/**
 * Public course catalogue, readable during SSR / prerender.
 *
 * /learning-journeys is the signed-in shell and is noindex, so the catalogue had
 * no indexable home. This exposes the same published/coming-soon courses through
 * the anon-readable columns so /courses can be prerendered with real content in
 * the first byte of HTML (crawlers never run our client code).
 */
import { createServerFn } from "@tanstack/react-start";
import { memoPublic } from "@/lib/serverMemo";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

export interface PublicCourse {
  id: string;
  slug: string;
  title: string;
  tagline: string | null;
  description: string | null;
  thumbnailUrl: string | null;
  status: string;
  minAge: number | null;
  maxAge: number | null;
  comingSoonDate: string | null;
  learningPoints: string[];
  levelName: string | null;
  levelOrder: number;
  orderIndex: number;
}

export interface PublicCourseLevel {
  name: string;
  description: string | null;
  courses: PublicCourse[];
}

export function courseSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^\da-z]+/g, "-")
    .replace(/^-|-$/g, "");
}

const COURSE_FIELDS =
  "id, title, description, thumbnail_url, status, min_age, max_age, coming_soon_date, parent_tagline, learning_point_1, learning_point_2, learning_point_3";

function publicClient() {
  // The prerender pass runs the built worker from a plain Node script, where the
  // Worker-injected process env is absent — fall back to the build-time inlined
  // VITE_* values so the static HTML still carries the real catalogue.
  const url = process.env["SUPABASE_URL"] || import.meta.env["VITE_SUPABASE_URL"];
  const key =
    process.env["SUPABASE_PUBLISHABLE_KEY"] || import.meta.env["VITE_SUPABASE_PUBLISHABLE_KEY"];
  if (!url || !key) return null;

  return createClient<Database>(url, key, {
    auth: { persistSession: false },
    global: {
      // sb_ publishable keys are opaque, not JWTs — send them as `apikey` only.
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) {
          headers.delete("Authorization");
        }
        headers.set("apikey", key);
        return fetch(input, { ...init, headers });
      },
    },
  });
}

export const getPublicCourses = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicCourseLevel[]> =>
    // Critical for /courses/$slug: 5 min fresh, then stale-while-revalidate, so
    // admin edits show within ~5 min and a slow DB never blanks the catalogue.
    (await memoPublic("public_courses", 300_000, 3_000, async (): Promise<PublicCourseLevel[]> => {
    const supabase = publicClient();
    if (!supabase) throw new Error("catalogue client unavailable");

    const [levelsRes, mappingRes] = await Promise.all([
      supabase
        .from("learning_journey_levels")
        .select("id, name, description, order_index")
        .eq("is_active", true)
        .order("order_index"),
      supabase
        .from("learning_journey_courses")
        .select(`level_id, order_index, courses!inner(${COURSE_FIELDS})`)
        .in("courses.status", ["published", "coming_soon"])
        .order("order_index"),
    ]);

    if (levelsRes.error || mappingRes.error) throw new Error("catalogue unavailable");

    const levels = levelsRes.data ?? [];
    const byLevel = new Map<string, PublicCourse[]>();

    for (const row of mappingRes.data ?? []) {
      const c = row.courses as unknown as Record<string, string | number | null>;
      if (!c) continue;
      const level = levels.find((l) => l.id === row.level_id);
      const course: PublicCourse = {
        id: String(c["id"]),
        slug: courseSlug(String(c["title"] ?? "")),
        title: String(c["title"] ?? ""),
        tagline: (c["parent_tagline"] as string | null) ?? null,
        description: (c["description"] as string | null) ?? null,
        thumbnailUrl: (c["thumbnail_url"] as string | null) ?? null,
        status: String(c["status"] ?? "published"),
        minAge: (c["min_age"] as number | null) ?? null,
        maxAge: (c["max_age"] as number | null) ?? null,
        comingSoonDate: (c["coming_soon_date"] as string | null) ?? null,
        learningPoints: [c["learning_point_1"], c["learning_point_2"], c["learning_point_3"]]
          .filter((p): p is string => typeof p === "string" && p.trim().length > 0),
        levelName: level?.name ?? null,
        levelOrder: level?.order_index ?? 999,
        orderIndex: Number(row.order_index ?? 0),
      };
      const key = level?.id ?? "unassigned";
      const list = byLevel.get(key) ?? [];
      list.push(course);
      byLevel.set(key, list);
    }

    const grouped: PublicCourseLevel[] = [];
    for (const level of levels) {
      const courses = byLevel.get(level.id);
      if (!courses?.length) continue;
      grouped.push({
        name: level.name,
        description: level.description ?? null,
        courses: courses.sort((a, b) => a.orderIndex - b.orderIndex),
      });
    }
    const unassigned = byLevel.get("unassigned");
    if (unassigned?.length) {
      grouped.push({ name: "More courses", description: null, courses: unassigned });
    }
    return grouped;
  }, { isValid: (levels) => levels.length > 0 })) ?? [],
);
